/**
 * index.tsx — Public landing route (`/`).
 *
 * General landing page — tidak menampilkan informasi peran/role apa saja
 * yang bisa login ke portal. Pengunjung hanya melihat deskripsi singkat
 * Coding Class dan tombol Masuk. Setelah login, sistem mendeteksi role
 * dan route ke dashboard yang sesuai (admin/tutor/siswa).
 */

import type { ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/')({
  component: LandingPage,
});

function LandingPage(): ReactNode {
  return (
    <main className="page-shell public-landing">
      <section className="hero hero-landing">
        <div className="hero-copy stack-lg">
          <p className="page-eyebrow">Coding Class · Invitation-Only</p>
          <h1>Belajar coding lewat game, kegiatan tanpa layar, dan project mini.</h1>
          <p className="hero-description">
            Coding Class adalah program kelas coding online untuk anak usia 6–12 tahun.
            Kurikulum berbasis game di layar, aktivitas off-screen seperti worksheet dan
            unplugged play, serta mini project yang melatih logika, pola, dan kreativitas.
          </p>
          <div className="cluster">
            <Link
              className="button button-primary"
              to="/login"
              search={{ error: undefined, redirect: undefined }}
            >
              Masuk dengan kode undangan
            </Link>
            <Link className="button button-secondary" to="/games">
              Lihat katalog game
            </Link>
          </div>
        </div>
        <div className="hero-art hero-art-landing hero-art-illustration">
          <img
            src="/images/generated/portal-hero-home/portal-hero-home.png"
            alt="Ilustrasi coding, worksheet, dan project mini untuk anak"
            width={1672}
            height={941}
          />
        </div>
      </section>

      <section className="section public-intro" aria-labelledby="tentang">
        <p className="section-eyebrow" id="tentang">
          Tentang
        </p>
        <h2 className="section-title">Program belajar coding untuk anak</h2>
        <p className="content-measure">
          Coding Class TerusMengajar menyajikan materi belajar berbasis game dan worksheet
          untuk anak usia 6–12 tahun. Kurikulum dirancang bertahap, dari konsep berpikir
          komputasional dasar hingga proyek mini yang melatih logika, pola, dan kreativitas.
        </p>
      </section>

      <section className="section public-pillars" aria-labelledby="pillars">
        <header className="section-header">
          <p className="section-eyebrow" id="pillars">
            Tiga pilar
          </p>
          <h2 className="section-title">Belajar coding dari tiga sudut yang berbeda</h2>
        </header>
        <div className="card-grid card-grid-3">
          <article className="card pillar-card pillar-card--layar">
            <span className="pillar-eyebrow" aria-hidden="true">
              01 · Layar
            </span>
            <h3 className="card-title">Berbasis game</h3>
            <p className="form-message">
              Mini game interaktif di browser yang memperkenalkan konsep coding lewat
              gameplay, bukan teks panjang.
            </p>
          </article>
          <article className="card pillar-card pillar-card--offscreen">
            <span className="pillar-eyebrow" aria-hidden="true">
              02 · Tanpa layar
            </span>
            <h3 className="card-title">Aktivitas off-screen</h3>
            <p className="form-message">
              Worksheet printable dan unplugged activity untuk latihan logika tanpa gawai.
              Cocok untuk di rumah atau kelas.
            </p>
          </article>
          <article className="card pillar-card pillar-card--project">
            <span className="pillar-eyebrow" aria-hidden="true">
              03 · Project
            </span>
            <h3 className="card-title">Mini project dengan Scratch Junior</h3>
            <p className="form-message">
              Proyek akhir yang mengajak anak merangkai ide jadi produk kecil di aplikasi
              Scratch Junior — bisa dibagikan ke keluarga atau teman sekelas.
            </p>
          </article>
        </div>
      </section>
    </main>
  );
}
